import { StrictMode, useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";

import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { DialogProvider } from "./components/DialogProvider";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CourierProvider } from "./contexts/CourierContext";
import { routeTree } from "./routeTree.gen";

import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import { queryClient } from "./lib/firestore/queryClient";
import { registerServiceWorker } from "./lib/registerServiceWorker";

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  scrollRestorationBehavior: "smooth",
  context: {
    auth: undefined!,
  },
});

Sentry.init({
  enabled: import.meta.env.PROD,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
  ],
  tracesSampleRate: 1.0,
  release: APP_VERSION,
  enableLogs: true,
});

if (import.meta.env.PROD) {
  const origError = console.error;
  const origWarn = console.warn;

  console.error = (...args: unknown[]) => {
    origError(...args);

    // Try to find a real Error object anywhere in args
    const err = args.find((a) => a instanceof Error) as Error | undefined;

    if (err) {
      Sentry.captureException(err);
    } else {
      // Fall back: join all args into one message
      Sentry.captureMessage(args.map(String).join(" "), "error");
    }
  };

  console.warn = (...args: unknown[]) => {
    origWarn(...args);
    Sentry.captureMessage(args.map(String).join(" "), "warning");
  };
}
// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const auth = useAuth();
  const authMemo = useMemo(() => auth, [auth]);
  useEffect(() => {
    router.invalidate();
  }, [authMemo]);
  return <RouterProvider router={router} context={{ auth }} />;
}

function CourierWrapper() {
  const { user } = useAuth();

  return (
    <CourierProvider userId={user?.uid || null}>
      <PostHogProvider
        apiKey={"phc_1ue24kx1z3Klx32XeQ4SYSghljYYXqvlfOBVnCpqmr3"}
        options={{
          api_host: "https://us.i.posthog.com",
        }}
      >
        <InnerApp />
      </PostHogProvider>
    </CourierProvider>
  );
}

const root = createRoot(document.getElementById("root")!);

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <DialogProvider>
        <AuthProvider>
          <CourierWrapper />
        </AuthProvider>
      </DialogProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// Register service worker with graceful error handling (doesn't block render)
registerServiceWorker();

export { Sentry };
